import { Radio, Stack, Checkbox, SimpleGrid, Textarea, TextInput, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { ActivationType, PrescriptionType } from 'models/prescription';
import { RadioBox } from 'components/RadioBox';
import { Label } from 'components/Label';
import { showFullName } from 'utils/helpers';
import { usePatient } from 'hooks/api/useUser';
import { useCreatePrescriptionFormContext } from 'context/FormContext';
import { PatientIdProps } from 'models/general';

export const FormPrescriptionActivation = ({ patientId }: PatientIdProps) => {
  const { t } = useTranslation();
  const { setFieldValue, values, getInputProps } = useCreatePrescriptionFormContext();
  const { data: patientData } = usePatient({ patientId });

  return (
    <Stack px={'md'}>
      <Title
        tt={'uppercase'}
        order={4}
        c={'var(--mantine-color-turquoise-9)'}
        mt={-20}
        data-testid={'title-prescription-activation'}
      >
        {t('activation_options_label')}
      </Title>
      {patientData && (
        <SimpleGrid cols={2}>
          <Label title={t('patient_info_label')} text={showFullName(patientData)} testId={'patient-name'} />
          <Label title={t('email_label')} text={patientData.email} testId={'patient-email'} />
        </SimpleGrid>
      )}
      <Radio.Group name={'activationType'} {...getInputProps('activationType')}>
        <Stack mb={'xs'}>
          {patientData && (
            <RadioBox
              value={ActivationType.Immediate}
              label={t('immediate_activation_label')}
              description={t('immediate_activation_description')}
              checked={values.activationType === ActivationType.Immediate}
              disabled={values.prescriptionType === PrescriptionType.Paid || !patientData}
              data-testid={`radio-box-activation-${ActivationType.Immediate}`}
            />
          )}
          <RadioBox
            value={ActivationType.Deferred}
            label={t('deferred_activation_label')}
            description={t('deferred_activation_description')}
            checked={values.activationType === ActivationType.Deferred}
            data-testid={`radio-box-activation-${ActivationType.Deferred}`}
          >
            <Checkbox
              disabled={values.activationType !== ActivationType.Deferred}
              label={t('add_prescript_download_label')}
              checked={!values.shouldSendEmail}
              onChange={() => setFieldValue('shouldSendEmail', !values.shouldSendEmail)}
              data-testid={`check-box-download-prescription`}
            />
            <Checkbox
              disabled={values.activationType !== ActivationType.Deferred}
              label={t('add_prescript_send_mail_label')}
              checked={values.shouldSendEmail}
              onChange={() => setFieldValue('shouldSendEmail', !values.shouldSendEmail)}
              data-testid={`check-box-email-prescription`}
            />
            <Textarea
              size={'xs'}
              minRows={11}
              autosize
              disabled={!values.shouldSendEmail || values.activationType === ActivationType.Immediate}
              {...getInputProps('emailBody')}
              data-testid={`text-box-email-body`}
            />
          </RadioBox>
        </Stack>
      </Radio.Group>
      {!patientData && (
        <TextInput
          disabled={!values.shouldSendEmail}
          label={t('email_label')}
          labelProps={{ 'data-testid': 'input-label-email' }}
          placeholder={t('email_label')}
          {...getInputProps('emailRecipient')}
          data-testid={`input-email`}
        />
      )}
    </Stack>
  );
};
